/**
 * Public Tracking Page
 * Customer-facing shipment tracking with temperature and humidity
 * No login required - accessed via QR code or tracking link
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Thermometer, Droplets, MapPin, Clock, Package, Truck,
  CheckCircle, AlertTriangle, Download, RefreshCw, Loader2,
  Phone, Mail, Shield, ArrowRight
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, ReferenceLine
} from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker } from 'react-leaflet';
import L from 'leaflet';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function PublicTrackingPage() {
  const { trackingCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [telemetry, setTelemetry] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const tempChartRef = useRef(null);
  const humidityChartRef = useRef(null);

  const fetchTrackingData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [trackRes, timelineRes, telemetryRes] = await Promise.all([
        axios.get(`${API_BASE}/api/v1/public/track/${trackingCode}`),
        axios.get(`${API_BASE}/api/v1/public/track/${trackingCode}/timeline`),
        axios.get(`${API_BASE}/api/v1/public/track/${trackingCode}/telemetry`)
      ]);
      
      setData(trackRes.data);
      setTimeline(timelineRes.data?.timeline || []);
      setTelemetry(telemetryRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch tracking data');
    } finally {
      setLoading(false);
    }
  }, [trackingCode]);

  useEffect(() => {
    if (trackingCode) {
      fetchTrackingData();
    }
  }, [trackingCode, fetchTrackingData]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return 'text-emerald-500';
      case 'warning': return 'text-amber-500';
      case 'breach': return 'text-red-500';
      default: return 'text-slate-400';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'normal': return 'bg-emerald-500/10 border-emerald-500/30';
      case 'warning': return 'bg-amber-500/10 border-amber-500/30';
      case 'breach': return 'bg-red-500/10 border-red-500/30';
      default: return 'bg-slate-500/10 border-slate-500/30';
    }
  };

  const downloadReport = async () => {
    try {
      setDownloadingPDF(true);
      
      // Fetch report data from API
      const res = await axios.get(`${API_BASE}/api/v1/public/track/${trackingCode}/report`);
      const reportData = res.data;
      
      // Create PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;
      
      // Helper function to add text
      const addText = (text, x, y, options = {}) => {
        pdf.setFontSize(options.size || 10);
        pdf.setTextColor(options.color || '#333333');
        if (options.bold) pdf.setFont('helvetica', 'bold');
        else pdf.setFont('helvetica', 'normal');
        pdf.text(text, x, y);
      };
      
      // Header with Track0tic branding
      pdf.setFillColor(30, 41, 59); // slate-800
      pdf.rect(0, 0, pageWidth, 35, 'F');
      
      addText('Track0tic', margin, 15, { size: 20, bold: true, color: '#ffffff' });
      addText('track verified Report', margin, 23, { size: 12, color: '#94a3b8' });
      addText(`Generated: ${new Date().toLocaleString()}`, pageWidth - margin - 50, 15, { size: 8, color: '#94a3b8' });
      
      yPos = 45;
      
      // Shipment Info Section
      pdf.setFillColor(248, 250, 252); // slate-50
      pdf.rect(margin, yPos, pageWidth - 2*margin, 35, 'F');
      pdf.setDrawColor(226, 232, 240);
      pdf.rect(margin, yPos, pageWidth - 2*margin, 35, 'S');
      
      addText('SHIPMENT DETAILS', margin + 5, yPos + 8, { size: 10, bold: true, color: '#475569' });
      addText(`Tracking Code: ${trackingCode}`, margin + 5, yPos + 16, { size: 10 });
      addText(`Status: ${reportData.shipment?.status?.toUpperCase() || 'N/A'}`, margin + 5, yPos + 23, { size: 10 });
      addText(`Origin: ${reportData.shipment?.origin || 'N/A'}`, margin + 5, yPos + 30, { size: 10 });
      
      addText(`Shipper: ${reportData.shipper?.name || 'N/A'}`, pageWidth/2 + 5, yPos + 16, { size: 10 });
      addText(`Started: ${reportData.shipment?.started_at ? new Date(reportData.shipment.started_at).toLocaleString() : 'N/A'}`, pageWidth/2 + 5, yPos + 23, { size: 10 });
      addText(`Duration: ${reportData.shipment?.duration_hours ? reportData.shipment.duration_hours + ' hrs' : 'In Progress'}`, pageWidth/2 + 5, yPos + 30, { size: 10 });
      
      yPos += 45;
      
      // Temperature Compliance Section
      const tempCompliant = reportData.temperature?.compliant;
      if (tempCompliant) {
        pdf.setFillColor(236, 253, 245); // green-50
      } else {
        pdf.setFillColor(254, 242, 242); // red-50
      }
      pdf.rect(margin, yPos, (pageWidth - 2*margin - 5)/2, 40, 'F');
      if (tempCompliant) {
        pdf.setDrawColor(167, 243, 208);
      } else {
        pdf.setDrawColor(254, 202, 202);
      }
      pdf.rect(margin, yPos, (pageWidth - 2*margin - 5)/2, 40, 'S');
      
      addText('TEMPERATURE', margin + 5, yPos + 8, { size: 9, bold: true, color: '#475569' });
      addText(tempCompliant ? 'COMPLIANT' : 'BREACH DETECTED', margin + 5, yPos + 16, { 
        size: 12, bold: true, color: tempCompliant ? '#059669' : '#dc2626' 
      });
      addText(`Required: ${reportData.temperature?.required_range || '2°C - 8°C'}`, margin + 5, yPos + 24, { size: 9 });
      addText(`Min: ${reportData.temperature?.min_recorded ?? 'N/A'}°C | Max: ${reportData.temperature?.max_recorded ?? 'N/A'}°C`, margin + 5, yPos + 31, { size: 9 });
      addText(`Avg: ${reportData.temperature?.avg_recorded ?? 'N/A'}°C | Readings: ${reportData.temperature?.total_readings || 0}`, margin + 5, yPos + 38, { size: 9 });
      
      // Humidity Compliance Section
      const humCompliant = reportData.humidity?.compliant;
      const humX = margin + (pageWidth - 2*margin)/2 + 2.5;
      if (humCompliant) {
        pdf.setFillColor(236, 253, 245);
      } else {
        pdf.setFillColor(254, 242, 242);
      }
      pdf.rect(humX, yPos, (pageWidth - 2*margin - 5)/2, 40, 'F');
      if (humCompliant) {
        pdf.setDrawColor(167, 243, 208);
      } else {
        pdf.setDrawColor(254, 202, 202);
      }
      pdf.rect(humX, yPos, (pageWidth - 2*margin - 5)/2, 40, 'S');
      
      addText('HUMIDITY', humX + 5, yPos + 8, { size: 9, bold: true, color: '#475569' });
      addText(humCompliant ? 'COMPLIANT' : 'BREACH DETECTED', humX + 5, yPos + 16, { 
        size: 12, bold: true, color: humCompliant ? '#059669' : '#dc2626' 
      });
      addText(`Required: ${reportData.humidity?.required_range || '35% - 65%'}`, humX + 5, yPos + 24, { size: 9 });
      addText(`Min: ${reportData.humidity?.min_recorded ?? 'N/A'}% | Max: ${reportData.humidity?.max_recorded ?? 'N/A'}%`, humX + 5, yPos + 31, { size: 9 });
      addText(`Avg: ${reportData.humidity?.avg_recorded ?? 'N/A'}% | Readings: ${reportData.humidity?.total_readings || 0}`, humX + 5, yPos + 38, { size: 9 });
      
      yPos += 50;
      
      // Temperature Chart
      if (tempChartRef.current) {
        addText('TEMPERATURE TREND', margin, yPos, { size: 11, bold: true, color: '#1e293b' });
        yPos += 5;
        
        try {
          const canvas = await html2canvas(tempChartRef.current, { 
            scale: 2, 
            backgroundColor: '#ffffff',
            logging: false 
          });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = pageWidth - 2*margin;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth, Math.min(imgHeight, 60));
          yPos += Math.min(imgHeight, 60) + 10;
        } catch (e) {
          addText('Chart could not be rendered', margin, yPos + 20, { size: 10, color: '#94a3b8' });
          yPos += 30;
        }
      }
      
      // Check if need new page for humidity chart
      if (yPos > pageHeight - 80) {
        pdf.addPage();
        yPos = margin;
      }
      
      // Humidity Chart  
      if (humidityChartRef.current) {
        addText('HUMIDITY TREND', margin, yPos, { size: 11, bold: true, color: '#1e293b' });
        yPos += 5;
        
        try {
          const canvas = await html2canvas(humidityChartRef.current, { 
            scale: 2, 
            backgroundColor: '#ffffff',
            logging: false 
          });
          const imgData = canvas.toDataURL('image/png');
          const imgWidth = pageWidth - 2*margin;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          
          pdf.addImage(imgData, 'PNG', margin, yPos, imgWidth, Math.min(imgHeight, 60));
          yPos += Math.min(imgHeight, 60) + 10;
        } catch (e) {
            // Silently handled
        }
      }
      
      // Footer
      const footerY = pageHeight - 10;
      pdf.setDrawColor(226, 232, 240);
      pdf.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      addText('Track0tic Cold Chain Monitoring by Track0tic', pageWidth/2, footerY, { size: 8, color: '#94a3b8' });
      
      // Save PDF
      pdf.save(`Track0tic_Report_${trackingCode}_${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (err) {
      alert('Failed to download report. Please try again.');
    } finally {
      setDownloadingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-slate-300">Loading shipment details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
          <AlertTriangle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-bold text-white mb-2">Shipment Not Found</h1>
          <p className="text-slate-400 mb-6">{error}</p>
          <Button onClick={fetchTrackingData} className="bg-blue-600 hover:bg-blue-700">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const { shipment, temperature, humidity, location, shipper } = data;
  const tempData = telemetry?.temperature?.data || [];
  const humidityData = telemetry?.humidity?.data || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/80 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-white">Track<span className="text-red-500">0</span>tic<sup className="text-[0.5em] align-super opacity-60">™</sup></h1>
              <p className="text-xs text-slate-400">Cold Chain Tracking</p>
            </div>
          </div>
          <Button onClick={fetchTrackingData} variant="ghost" size="sm" className="text-slate-300">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Status Banner */}
        <div className={cn(
          "rounded-2xl p-6 border",
          shipment.status === 'completed' ? 'bg-emerald-500/10 border-emerald-500/30' :
          shipment.status === 'in_progress' ? 'bg-blue-500/10 border-blue-500/30' :
          'bg-slate-700/50 border-slate-600'
        )}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {shipment.status === 'completed' ? (
                <CheckCircle className="w-12 h-12 text-emerald-500" />
              ) : shipment.status === 'in_progress' ? (
                <Truck className="w-12 h-12 text-blue-500" />
              ) : (
                <Package className="w-12 h-12 text-slate-400" />
              )}
              <div>
                <p className="text-sm text-slate-400">Shipment Status</p>
                <h2 className="text-2xl font-bold text-white">{shipment.status_display}</h2>
                {shipment.eta && shipment.status === 'in_progress' && (
                  <p className="text-sm text-blue-400 flex items-center gap-1 mt-1">
                    <Clock className="w-4 h-4" />
                    ETA: {new Date(shipment.eta).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                )}
              </div>
            </div>
            <Badge className="text-lg px-4 py-2 bg-slate-700 text-white border-slate-600">
              {trackingCode}
            </Badge>
          </div>
        </div>

        {/* Route Info */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">From</p>
              <p className="text-white font-medium">{shipment.origin || 'Origin'}</p>
            </div>
            <div className="flex-shrink-0">
              <ArrowRight className="w-6 h-6 text-blue-500" />
            </div>
            <div className="flex-1 text-right">
              <p className="text-xs text-slate-400 uppercase tracking-wider mb-1">To</p>
              <p className="text-white font-medium">{shipment.destination || 'Destination'}</p>
            </div>
          </div>
        </div>

        {/* Route Map */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden" data-testid="route-map">
          <div className="p-4 border-b border-slate-700 flex items-center justify-between">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-400" />Shipment Route
            </h3>
            <Badge className={shipment.status === 'delivered' ? 'bg-green-900/30 text-green-400' : 'bg-blue-900/30 text-blue-400'}>
              {shipment.status || 'In Transit'}
            </Badge>
          </div>
          <div className="h-[250px]">
            <MapContainer
              center={[
                shipment.origin_lat ? parseFloat(shipment.origin_lat) : 28.6139,
                shipment.origin_lng ? parseFloat(shipment.origin_lng) : 77.2090
              ]}
              zoom={11}
              style={{ height: '100%', width: '100%' }}
              className="z-0"
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap'
              />
              {/* Origin marker */}
              {shipment.origin_lat && shipment.origin_lng && (
                <CircleMarker
                  center={[parseFloat(shipment.origin_lat), parseFloat(shipment.origin_lng)]}
                  radius={8}
                  pathOptions={{ fillColor: '#8b5cf6', color: '#8b5cf6', weight: 2, fillOpacity: 0.9 }}
                >
                  <Popup><div className="text-sm"><p className="font-bold text-violet-600">Origin</p><p>{shipment.origin}</p></div></Popup>
                </CircleMarker>
              )}
              {/* Destination marker */}
              {shipment.dest_lat && shipment.dest_lng && (
                <CircleMarker
                  center={[parseFloat(shipment.dest_lat), parseFloat(shipment.dest_lng)]}
                  radius={8}
                  pathOptions={{ fillColor: '#f97316', color: '#f97316', weight: 2, fillOpacity: 0.9 }}
                >
                  <Popup><div className="text-sm"><p className="font-bold text-orange-600">Destination</p><p>{shipment.destination}</p></div></Popup>
                </CircleMarker>
              )}
              {/* Current device position */}
              {shipment.device_lat && shipment.device_lng && (
                <CircleMarker
                  center={[parseFloat(shipment.device_lat), parseFloat(shipment.device_lng)]}
                  radius={10}
                  pathOptions={{ fillColor: '#10b981', color: '#10b981', weight: 3, fillOpacity: 0.9 }}
                >
                  <Popup>
                    <div className="text-sm">
                      <p className="font-bold text-green-600">Current Location</p>
                      <p>Device: {shipment.device_uid || 'Active'}</p>
                      {shipment.current_temp && <p>Temp: {shipment.current_temp}°C</p>}
                    </div>
                  </Popup>
                </CircleMarker>
              )}
              {/* Route line */}
              {shipment.origin_lat && shipment.dest_lat && (
                <Polyline
                  positions={[
                    [parseFloat(shipment.origin_lat), parseFloat(shipment.origin_lng)],
                    ...(shipment.device_lat ? [[parseFloat(shipment.device_lat), parseFloat(shipment.device_lng)]] : []),
                    [parseFloat(shipment.dest_lat), parseFloat(shipment.dest_lng)]
                  ]}
                  color="#3b82f6"
                  weight={2}
                  opacity={0.6}
                  dashArray="8, 12"
                />
              )}
            </MapContainer>
          </div>
          <div className="px-4 py-2 bg-slate-800/50 flex items-center gap-4 text-xs text-slate-400 border-t border-slate-700">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-violet-500" />Origin</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Current</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500" />Destination</span>
          </div>
        </div>

        {/* Live Cold Chain Status */}
        <div className="grid grid-cols-2 gap-4">
          {/* Temperature Card */}
          <div className={cn(
            "rounded-2xl p-5 border",
            getStatusBg(temperature.status)
          )}>
            <div className="flex items-center justify-between mb-3">
              <Thermometer className={cn("w-8 h-8", getStatusColor(temperature.status))} />
              {temperature.compliant ? (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Compliant</Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Breach</Badge>
              )}
            </div>
            <p className="text-sm text-slate-400 mb-1">Temperature</p>
            <p className={cn("text-4xl font-bold", getStatusColor(temperature.status))}>
              {temperature.current !== null ? `${temperature.current}°C` : 'N/A'}
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Required: {temperature.range?.min}°C - {temperature.range?.max}°C
            </p>
          </div>

          {/* Humidity Card */}
          <div className={cn(
            "rounded-2xl p-5 border",
            getStatusBg(humidity.status)
          )}>
            <div className="flex items-center justify-between mb-3">
              <Droplets className={cn("w-8 h-8", getStatusColor(humidity.status))} />
              {humidity.compliant ? (
                <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">Compliant</Badge>
              ) : (
                <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Breach</Badge>
              )}
            </div>
            <p className="text-sm text-slate-400 mb-1">Humidity</p>
            <p className={cn("text-4xl font-bold", getStatusColor(humidity.status))}>
              {humidity.current !== null ? `${humidity.current}%` : 'N/A'}
            </p>
            <p className="text-xs text-slate-500 mt-2">
              Required: {humidity.range?.min}% - {humidity.range?.max}%
            </p>
          </div>
        </div>

        {/* Temperature Graph */}
        {tempData.length > 0 && (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h3 className="font-semibold text-white mb-4">Temperature History</h3>
            <div ref={tempChartRef} className="h-[200px] bg-white rounded-lg p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={tempData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="timestamp" 
                    tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    stroke="#64748b"
                    fontSize={12}
                  />
                  <YAxis domain={[0, 15]} stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                    labelFormatter={(v) => new Date(v).toLocaleString()}
                  />
                  <ReferenceLine y={temperature.range?.min || 2} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Min', fill: '#f59e0b', fontSize: 10 }} />
                  <ReferenceLine y={temperature.range?.max || 8} stroke="#f59e0b" strokeDasharray="5 5" label={{ value: 'Max', fill: '#f59e0b', fontSize: 10 }} />
                  <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={false} name="Temperature (°C)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {telemetry?.temperature?.stats && (
              <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-700">
                <div className="text-center">
                  <p className="text-xs text-slate-400">Min</p>
                  <p className="text-lg font-bold text-white">{telemetry.temperature.stats.min}°C</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400">Max</p>
                  <p className="text-lg font-bold text-white">{telemetry.temperature.stats.max}°C</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400">Avg</p>
                  <p className="text-lg font-bold text-white">{telemetry.temperature.stats.avg}°C</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400">Readings</p>
                  <p className="text-lg font-bold text-white">{telemetry.temperature.stats.readings}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Humidity Graph */}
        {humidityData.length > 0 && (
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <h3 className="font-semibold text-white mb-4">Humidity History</h3>
            <div ref={humidityChartRef} className="h-[200px] bg-white rounded-lg p-2">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={humidityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis 
                    dataKey="timestamp" 
                    tickFormatter={(v) => new Date(v).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    stroke="#64748b"
                    fontSize={12}
                  />
                  <YAxis domain={[0, 100]} stroke="#64748b" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                    labelFormatter={(v) => new Date(v).toLocaleString()}
                  />
                  <ReferenceLine y={humidity.range?.min || 35} stroke="#06b6d4" strokeDasharray="5 5" label={{ value: 'Min', fill: '#06b6d4', fontSize: 10 }} />
                  <ReferenceLine y={humidity.range?.max || 65} stroke="#06b6d4" strokeDasharray="5 5" label={{ value: 'Max', fill: '#06b6d4', fontSize: 10 }} />
                  <Line type="monotone" dataKey="value" stroke="#06b6d4" strokeWidth={2} dot={false} name="Humidity (%)" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            {telemetry?.humidity?.stats && (
              <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t border-slate-700">
                <div className="text-center">
                  <p className="text-xs text-slate-400">Min</p>
                  <p className="text-lg font-bold text-white">{telemetry.humidity.stats.min}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400">Max</p>
                  <p className="text-lg font-bold text-white">{telemetry.humidity.stats.max}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400">Avg</p>
                  <p className="text-lg font-bold text-white">{telemetry.humidity.stats.avg}%</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-400">Readings</p>
                  <p className="text-lg font-bold text-white">{telemetry.humidity.stats.readings}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Timeline */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
          <h3 className="font-semibold text-white mb-4">Shipment Timeline</h3>
          <div className="space-y-4">
            {timeline.map((event, idx) => (
              <div key={`idx-${idx}`} className="flex gap-4">
                <div className="flex flex-col items-center">
                  <div className={cn(
                    "w-3 h-3 rounded-full",
                    event.completed ? "bg-emerald-500" : event.current ? "bg-blue-500 animate-pulse" : "bg-slate-600"
                  )} />
                  {idx < timeline.length - 1 && (
                    <div className={cn(
                      "w-0.5 flex-1 my-1",
                      event.completed ? "bg-emerald-500" : "bg-slate-600"
                    )} />
                  )}
                </div>
                <div className="flex-1 pb-4">
                  <p className={cn(
                    "font-medium",
                    event.completed ? "text-white" : event.current ? "text-blue-400" : "text-slate-400"
                  )}>
                    {event.title}
                  </p>
                  {event.description && (
                    <p className="text-sm text-slate-400">{event.description}</p>
                  )}
                  {event.timestamp && (
                    <p className="text-xs text-slate-500 mt-1">
                      {new Date(event.timestamp).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Download Report */}
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-white">track verified Report</h3>
              <p className="text-sm text-slate-400">Download complete temperature and humidity log with graphs</p>
            </div>
            <Button 
              onClick={downloadReport} 
              disabled={downloadingPDF}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {downloadingPDF ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Shipper Info */}
        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6">
          <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Shipped by</p>
          <p className="text-white font-semibold">{shipper?.name || 'Shipper'}</p>
          <p className="text-sm text-slate-400 mt-4">
            Powered by Track<span className="text-red-500">0</span>tic<sup className="text-[0.5em] align-super opacity-60">™</sup> Cold Chain Monitoring
          </p>
        </div>
      </main>
    </div>
  );
}
